import { useTranslation } from 'react-i18next'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    Stack,
    TextField,
    MenuItem,
} from "@mui/material";
import {  cutomDbEncodingConstant } from '../../../constants/constantList';
import CommonGrid from '../../Repository/CommonGrid';
import { cloneDeep, debounce } from 'lodash';
import LightTooltip from '../../../commonComponents/LightTooltip';
import { ParentScreenCtx } from '../../../pages/workbench_build/WorkbenchBuild';
import { eachDeep } from "deepdash-es/standalone";
import { useDispatch, useSelector } from 'react-redux';
import { treeActions } from '../../../redux/slices/treeSlice';
import { removeLowerCaseAndUndefined } from '../../../utils/utilFunctions';

const REQUIRED_ITEMS = [
    { ARGNAME: 'RESULT_COUNT', MAPNAME: 'RESULT_COUNT', FIELD_NAME: 'RESULT_COUNT' },
    { ARGNAME: 'CURRENT_RECORD_INDEX', MAPNAME: 'CURRENT_RECORD_INDEX', FIELD_NAME: 'CURRENT_RECORD_INDEX' },
    { ARGNAME: 'SQL_ERROR', MAPNAME: 'SQL_ERROR', FIELD_NAME: 'SQL_ERROR' }
];

const ColumnReturnValues = ({ columnReturnValues }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const parentScreen = useContext(ParentScreenCtx);
    const { rightTree } = useSelector((state) => state.treeData[parentScreen]);
    const translatedData = rightTree.tree[rightTree.currentTab].translatedData;
    const selectedNode = rightTree.tree[rightTree.currentTab].selectedNode;
    const containerRef = useRef(null);
    const [databaseColumn, setDatabaseColumn] = useState('');
    const [fieldName, setFieldName] = useState('');
    const [encoding, setEncoding] = useState(Object.values(cutomDbEncodingConstant)[0]);
    const [rowData, setRowData] = useState([]);
    const [gridApi, setGridApi] = useState('');
    const [selectedColumn, setSelectedColumn] = useState({});
    const selectedColumnRef = useRef(selectedColumn);
    const [updatedColumnValues, setUpdatedColumnValues] = useState([]);
    const selectedNodeRef = useRef(selectedNode);
    const translatedDataRef = useRef(translatedData);

    const submitChange = (key, val, currentSelectedNode, currentTranslatedData) => {
        if (key !== 'OUT_ARG') return;
        val = val.map((item) => removeLowerCaseAndUndefined(item));

        const updatedData = eachDeep(cloneDeep(currentTranslatedData), (objValue) => {
            if (objValue?.key !== currentSelectedNode?.key) {
                return;
            }

            if (objValue && key === 'OUT_ARG') {
                if (!Object.isExtensible(objValue)) {
                    objValue = { ...objValue };
                }
                objValue[key] = val;
                dispatch(
                    treeActions.setRightTreeSelectedNode({
                        screen: parentScreen,
                        type: rightTree.currentTab,
                        data: objValue,
                    })
                );
            }
        });
        dispatch(
            treeActions.setNewRightTreeTranslatedData({
                screen: parentScreen,
                type: rightTree.currentTab,
                data: updatedData,
            })
        );
    };

    const debounceChangeHandler = useCallback(
        debounce((key, val) => {
            submitChange(key, val, selectedNodeRef.current, translatedDataRef.current);
        }, 200),
        []
    );

    const columnHandler = (data = [], selectionIndex, gridAPI) => {
        if (!gridAPI) {
            setRowData([]);
            return;
        }
        let newEntries = [];
        if (data?.length === 0) {
            newEntries = [...REQUIRED_ITEMS];
        } else {
            REQUIRED_ITEMS.forEach((item) => {
                const isPresent = data.some(
                    (dataItem) =>
                        dataItem.ARGNAME === item.ARGNAME &&
                        dataItem.MAPNAME === item.MAPNAME &&
                        dataItem.FIELD_NAME === item.FIELD_NAME
                );

                if (!isPresent) {
                    newEntries.push(item);
                }
            });
        }
        const uniqueNewEntries = newEntries.filter(newEntry =>
            !data.some(existingEntry => existingEntry.MAPNAME === newEntry.MAPNAME)
        );
        const newData = [...data, ...uniqueNewEntries];
        const rowData = newData?.map((e, i) => {
            return { ...e, key: e.MAPNAME, id: i };
        });

        gridAPI.setGridOption('rowData', rowData)
            setUpdatedColumnValues(rowData);
        setTimeout(() => {
            gridAPI.forEachNode((node) => {
                if (node?.data?.id === selectionIndex) {
                    node.setSelected(true);
                    setSelectedColumn(node.data);
                    gridAPI.ensureNodeVisible(node);
                }
            });

            if (!rowData?.length) setSelectedColumn({});
        }, 100);
    };

    const submitHandler = (key, value) => {
        const nodes = updatedColumnValues.map((node) => {
            if (node?.id !== selectedColumn?.id) return node;
            const updated = {
                ...node,
                [key]: value,
            };
            if (key === "ARGNAME") {
                updated.MAPNAME = value;
                updated.key = value; // update grid display value
            }
            return updated;
        });

        setUpdatedColumnValues(nodes);
        if (gridApi) {
            // Lightweight in-place update to avoid heavy reinitialization/flicker
            gridApi.setGridOption("rowData", nodes);
            setRowData(nodes);
            const currentSelectedId = selectedColumnRef.current?.id;
            gridApi.forEachNode((node) => {
                if (node?.data?.id === currentSelectedId) {
                    node.setSelected(true);
                }
            });
        }
        debounceChangeHandler("OUT_ARG", nodes);
    };

    const createNodeTitle = (e) => {
        let title = 'UNNAMED1';
        const numberedTitles = new Set();
        e.api.forEachNode(node => {
            if (node?.data?.ARGNAME.includes('UNNAMED')) {
                const match = node?.data?.ARGNAME.match(/^UNNAMED(\d+)$/);
                if (match) {
                    const number = parseInt(match[1]);
                    numberedTitles.add(`UNNAMED${number}`);
                }
            }
        });
        for (let i = 1; i <= numberedTitles.size + 1; i++) {
            const candidateTitle = `UNNAMED${i}`;
            if (!numberedTitles.has(candidateTitle)) {
                title = candidateTitle;
                break;
            }
        }
        return title;
    };

    const customClickHandlerOnAdd = (e) => {
        const title = createNodeTitle(e);
        const newColumn = {
            ARGNAME: title,
            ARGTYPE: 'OUT',
            FIELD_NAME: title,
            ICON_DISPLAY: 'OUT_ARG',
            MAPNAME: title,
            ENCODING: Object.values(cutomDbEncodingConstant)[0],
        };
        const data = [];
        const selectedRows = e.api.getSelectedRows();
        e.api.forEachNode((node) => {
            data.push(node.data);
        });
        data.splice(+(selectedRows?.[0]?.id || 0) + 1, 0, {
            ...newColumn,
        });
        columnHandler(
            [...data],
            data.length && selectedRows?.length ? +selectedRows?.[0]?.id + 1 : 0,
            e.api
        );
        debounceChangeHandler('OUT_ARG', data);
    };

    const handleRow = (options) => {
        const selectedRows = options.api.getSelectedRows();

        // If user clicks on empty space and selection is cleared, reselect previous row
        if (!selectedRows || !selectedRows.length) {
            const prev = selectedColumnRef.current;
            if (prev && prev.id !== undefined) {
                options.api.forEachNode((node) => {
                    if (node?.data?.id === prev.id) {
                        node.setSelected(true);
                    }
                });
            }
            return;
        }

        const sel = { ...selectedRows[0] };
        setSelectedColumn(sel);
        selectedColumnRef.current = sel;
        options.api.redrawRows();
    }

    const deleteActionHandler = (e) => {
        const data = [];
        e.api.forEachNode((node) => {
            if (e.data.id !== node.data.id) data.push(node.data);
        });
        columnHandler([...data], 0, e.api);
        debounceChangeHandler('OUT_ARG', data);
    };

    const CustomCellRenderer = (info) => {
        const { value, data } = info;
        return (
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    height: "2rem",
                }}
            >
                <div className='truncated-text' title={value}>
                    {value}
                </div>
                {!['RESULT_COUNT', 'CURRENT_RECORD_INDEX', 'SQL_ERROR']?.includes(value) && <div className='flex align-center'>
                    <LightTooltip onClick={() => deleteActionHandler(info)}>
                        <span
                            style={{ cursor: "pointer" }}
                            className='DELETE_MUI'
                        ></span>
                    </LightTooltip>
                </div>}
            </div>
        );
    };

    const gridOptions = {
        columnDefs: [
            {
                addNewControl: true,
                addControlLabel: t("LBL_COLUMNS_RETURN_VALUES"),
                headerName: t("LBL_COLUMNS_RETURN_VALUES"),
                field: "key",
                customClickHandlerOnAdd: customClickHandlerOnAdd,
                cellRenderer: (info) => CustomCellRenderer(info),
                editable: false,
                resizable: false,
            },
        ],
        suppressMovableColumns: true,
    };

    const encodingOptions = Object.keys(cutomDbEncodingConstant).map((e, i) => {
        return (
            <MenuItem value={cutomDbEncodingConstant[e]} key={i}>
                {t(cutomDbEncodingConstant[e])}
            </MenuItem>
        );
    });

    useEffect(() => {
        setDatabaseColumn(selectedColumn?.ARGNAME || '')
        setFieldName(selectedColumn?.FIELD_NAME || '' || '')
        setEncoding(selectedColumn?.ENCODING ? selectedColumn?.ENCODING : Object.values(cutomDbEncodingConstant)[0]);
        if (selectedColumnRef.current !== selectedColumn) {
            selectedColumnRef.current = selectedColumn;
        }
    }, [selectedColumn]);

    useEffect(() => {
        columnHandler(columnReturnValues || [], 0, gridApi);
    }, [columnReturnValues, gridApi])

    useEffect(() => {
        if (selectedNodeRef.current !== selectedNode) {
            selectedNodeRef.current = selectedNode;
        }
    }, [selectedNode]);

    useEffect(() => {
        if (translatedDataRef.current !== translatedData) {
            translatedDataRef.current = translatedData;
        }
    }, [translatedData]);

    return (
        <Box sx={{ padding: "8px" }}>
            <Stack spacing={"15px"}>
                <Box className="div-textBox-action" ref={containerRef}>
                    <CommonGrid
                        gridOptions={gridOptions}
                        rowData={rowData}
                        getGridAPI={(gridOptions, gridApi) => {
                            setGridApi(gridApi.api)
                            columnHandler(columnReturnValues || [], 0, gridApi.api);
                        }}
                        containerStyle={{ height: "100% !important" }}
                        gridResizable={true}
                        gridProps={{
                            rowSelection: 'single',
                            onSelectionChanged: handleRow,
                        }}
                    />
                </Box>
                {columnReturnValues?.length !== 0 && <>
                    <FormControl>
                        <TextField
                            variant="outlined"
                            size="small"
                            label={t("LBL_COL_NM")}
                            type="text"
                            className={"width-100"}
                            name={"COL_NM"}
                            value={databaseColumn}
                            autoComplete="off"
                            onChange={(e) => {
                                setDatabaseColumn(e.target.value);
                            }}
                            onBlur={(e) => {
                                submitHandler('ARGNAME', e.target.value);
                            }}
                        />
                    </FormControl>
                    <FormControl>
                        <TextField
                            variant="outlined"
                            size="small"
                            label={t("LBL_FLD_NM_TO_USE")}
                            type="text"
                            className={"width-100"}
                            name={"FLD_NM_TO_USE"}
                            value={fieldName}
                            autoComplete="off"
                            onChange={(e) => {
                                setFieldName(e.target.value);
                            }}
                            onBlur={(e) => {
                                submitHandler('FIELD_NAME', e.target.value);
                            }}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="width-100" size="small">
                        <InputLabel>{t("ENCODING")}</InputLabel>
                        <Select
                            className={"width-100"}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        maxHeight: 300,
                                    },
                                },
                            }}
                            value={encoding}
                            onChange={(e) => {
                                setEncoding(e.target.value);
                                submitHandler('ENCODING', e.target.value);
                            }}
                        >
                            {encodingOptions}
                        </Select>
                    </FormControl>
                </>}
            </Stack>
        </Box>
    )
}

export default ColumnReturnValues